import { useEffect } from "react";

import {
  Box,
  Button,
  CheckIcon,
  Column,
  Dialog,
  ExternalLinkIcon,
  Row,
  Text,
  useToast,
} from "@hightouch/htui";
import { useAcceptCepTosMutation } from "src/graphql";
import * as analytics from "src/lib/analytics";
import { Link, useNavigate } from "src/router";

import { useShouldShowCepTos } from "src/cep/hooks/use-should-show-cep-tos";

const CEP_TERMS_URL = "https://hightouch.com/cep-terms";

const CHECKLIST_ITEMS = [
  "Send email, SMS, and push messages straight from your warehouse",
  "Build campaigns and journeys without copying data into a separate ESP",
  "Keep deliverability, content, and audiences in one place",
];

export const CepTermsOfService = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { shouldShowCepTos } = useShouldShowCepTos();
  const acceptCepTos = useAcceptCepTosMutation();

  useEffect(() => {
    if (!shouldShowCepTos) return;
    analytics.track("Native Delivery ToS Modal Shown");
  }, [shouldShowCepTos]);

  if (!shouldShowCepTos) return null;

  const accept = async () => {
    try {
      await acceptCepTos.mutateAsync({});
    } catch {
      toast({
        id: "cep-tos-error",
        title: "Something went wrong. Please try again.",
        variant: "error",
      });
      return;
    }
    analytics.track("Native Delivery ToS Modal Accepted");
  };

  // Declining or closing without accepting leaves Native Delivery rather than exposing the un-accepted UI
  const decline = () => {
    analytics.track("Native Delivery ToS Modal Declined");
    navigate("/");
  };

  return (
    <Dialog
      isOpen
      variant="info"
      title="Introducing Native Delivery"
      width="lg"
      onClose={decline}
      actions={
        <Row gap={2}>
          <Button isDisabled={acceptCepTos.isLoading} onClick={decline}>
            Decline
          </Button>
          <Button
            variant="primary"
            isLoading={acceptCepTos.isLoading}
            onClick={accept}
          >
            Get started
          </Button>
        </Row>
      }
    >
      <Column gap={4}>
        <Text color="text.secondary">
          Send messages to your customers directly from your warehouse, with no
          third-party messaging platform required.
        </Text>
        <Column gap={2}>
          {CHECKLIST_ITEMS.map((item) => (
            <Row key={item} gap={2} align="flex-start">
              <Box
                as={CheckIcon}
                color="text.success"
                flexShrink={0}
                mt="2px"
              />
              <Text color="text.secondary">{item}</Text>
            </Row>
          ))}
        </Column>
        <Text color="text.secondary">
          Use of this feature is subject to{" "}
          <Link href={CEP_TERMS_URL} isExternal>
            Hightouch's Native Delivery terms
            <Box as={ExternalLinkIcon} ml={1} mb="1px" />
          </Link>
        </Text>
      </Column>
    </Dialog>
  );
};
